import { getTranslatedProperty } from "@shopware/helpers"
import type { Schemas } from "#shopware"

/**
 * Composable for generating Schema.org Product structured data (JSON-LD)
 *
 * @param product - Reactive reference to Shopware Product
 * @returns Object containing schema and jsonLd computed properties
 *
 * @example
 * const { jsonLd } = useProductStructuredData(productRef)
 *
 * // In template:
 * <script type="application/ld+json" v-html="jsonLd" />
 */
export function useProductStructuredData(product: Ref<Schemas["Product"]>) {
    const route = useRoute()
    const config = useRuntimeConfig()

    /**
     * Build absolute URL for the product
     */
    const productUrl = computed(() => {
        const baseUrl = config.public.shopware?.devStorefrontUrl || window.location.origin
        return `${baseUrl}${route.fullPath}`
    })

    /**
     * Get translated product name
     */
    const productName = computed(() => getTranslatedProperty(product.value, 'name'))

    /**
     * Get translated product description
     */
    const productDescription = computed(() => getTranslatedProperty(product.value, 'description'))

    /**
     * Get main product image URL (absolute)
     */
    const productImage = computed(() => {
        if (!product.value.cover?.media?.url) return null

        const baseUrl = config.public.shopware?.devStorefrontUrl || window.location.origin
        const imageUrl = product.value.cover.media.url

        return imageUrl.startsWith('http') ? imageUrl : `${baseUrl}${imageUrl}`
    })

    /**
     * Get additional product images (absolute URLs)
     */
    const productImages = computed(() => {
        if (!product.value.media || product.value.media.length === 0) return []

        const baseUrl = config.public.shopware?.devStorefrontUrl || window.location.origin

        return product.value.media
            .filter((media: Schemas["ProductMedia"]) => media.media?.url)
            .map((media: Schemas["ProductMedia"]) => {
                const url = media.media!.url
                return url.startsWith('http') ? url : `${baseUrl}${url}`
            })
    })

    /**
     * Build Brand schema
     */
    const brand = computed(() => {
        if (!product.value.manufacturer?.name) return null

        return {
            '@type': 'Brand',
            name: product.value.manufacturer.name
        }
    })

    /**
     * Build AggregateRating schema
     */
    const aggregateRating = computed(() => {
        if (!product.value.ratingAverage || product.value.ratingAverage === 0) return null

        return {
            '@type': 'AggregateRating',
            ratingValue: product.value.ratingAverage,
            bestRating: 5,
            worstRating: 1,
            reviewCount: product.value.ratingCount || 0
        }
    })

    /**
     * Determine availability status
     */
    const availabilityStatus = computed(() => {
        if (!product.value.available) return 'https://schema.org/OutOfStock'

        if (product.value.availableStock && product.value.availableStock > 0) {
            return 'https://schema.org/InStock'
        }

        if (product.value.isCloseout && product.value.availableStock === 0) {
            return 'https://schema.org/OutOfStock'
        }

        return 'https://schema.org/InStock'
    })

    /**
     * Build Offer or AggregateOffer schema based on pricing structure
     */
    const offers = computed(() => {
        const calculatedPrice = product.value.calculatedPrice

        if (!calculatedPrice) return null

        // Base offer properties
        const baseOffer = {
            url: productUrl.value,
            priceCurrency: 'EUR',
            availability: availabilityStatus.value,
            itemCondition: 'https://schema.org/NewCondition',
            // Price valid until end of year (common practice)
            priceValidUntil: new Date(new Date().getFullYear(), 11, 31).toISOString().split('T')[0]
        }

        // Otherwise use single Offer
        return {
            '@type': 'Offer',
            ...baseOffer,
            price: calculatedPrice.unitPrice,
            // Add list price if available (shows discount)
            ...(calculatedPrice.listPrice?.price && {
                priceSpecification: {
                    '@type': 'UnitPriceSpecification',
                    price: calculatedPrice.listPrice.price,
                    priceCurrency: 'EUR'
                }
            })
        }
    })

    /**
     * Build complete Product schema object
     */
    const schema = computed(() => {
        const schemaObj: Record<string, unknown> = {
            '@context': 'https://schema.org',
            '@type': 'Product',
            name: productName.value,
            url: productUrl.value
        }

        // Add description if available
        if (productDescription.value) {
            schemaObj.description = productDescription.value
        }

        // Add main image
        if (productImage.value) {
            schemaObj.image = productImages.value.length > 0
                ? [productImage.value, ...productImages.value]
                : productImage.value
        }

        // Add product identifiers
        if (product.value.productNumber) {
            schemaObj.sku = product.value.productNumber
        }

        if (product.value.ean) {
            schemaObj.gtin13 = product.value.ean
        }

        if (product.value.manufacturerNumber) {
            schemaObj.mpn = product.value.manufacturerNumber
        }

        // Add brand
        if (brand.value) {
            schemaObj.brand = brand.value
        }

        // Add aggregate rating
        if (aggregateRating.value) {
            schemaObj.aggregateRating = aggregateRating.value
        }

        // Add offers
        if (offers.value) {
            schemaObj.offers = offers.value
        }

        return schemaObj
    })

    /**
     * Serialize schema to JSON-LD string
     */
    const jsonLd = computed(() => JSON.stringify(schema.value))

    return {
        schema,
        jsonLd,
        // Export individual parts for testing/debugging
        productUrl,
        productName,
        productDescription,
        productImage,
        productImages,
        brand,
        aggregateRating,
        availabilityStatus,
        offers
    }
}
